import React, { useState } from "react";

import {
  InputChangeEvent,
  IScheduledTaskPropertyRef,
} from "api/model/airflow-task/task-template/TaskTemplateInfo";
import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import { Column, ColumnEditorOptions } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import { DataTable, DataTableRowEditCompleteEvent, DataTableRowEditEvent } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { v4 } from "uuid";

interface ITaskConfigNamedDatasetTableProps extends IScheduledTaskPropertyRef {
  header: string;
  datasets: Record<string, string>;
  onChange: (e: InputChangeEvent<Record<string, string>>) => void;
  bodyClass?: string;
}

interface INamedDatasetRowModel {
  key: string;
  value: { dataset: string | null } | null;
  newValue: { dataset: string | null } | null;
  id: string;
}

export default function TaskConfigNamedDatasetTable(props: ITaskConfigNamedDatasetTableProps) {
  const [dataArr, setDataArr] = useState<INamedDatasetRowModel[]>(mapToList(props.datasets));
  const [editingRows, setEditingRows] = useState<Record<string, boolean>>({});

  const propagateChangesStateUp = (arr: INamedDatasetRowModel[]) => {
    const c: Record<string, string> = {};

    arr.forEach(e => {
      if (e.value) c[e.key] = e.value.dataset!; // identifier must be defined
    });

    props.onChange({ property: props.property, value: c });
  };

  const confirmWrapper = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    confirmHandler: () => void
  ) => {
    confirmPopup({
      target: event.currentTarget,
      message: T("confirm-popup.are-you-really-want-to-delete-this-dataset-mapping"),
      icon: "pi pi-exclamation-triangle",
      accept: confirmHandler,
    });
  };

  const validate = (data: INamedDatasetRowModel) => {
    if (data?.newValue?.dataset === null) return false;
    return true;
  };

  const handleRowEditInit = (e: DataTableRowEditEvent) => {
    const data = e.data as INamedDatasetRowModel;
    data.newValue = { ...data.value! };
  };

  const handleEditCancel = (e: DataTableRowEditEvent) => {
    const data = e.data as INamedDatasetRowModel;
    if (data.newValue?.dataset === null || data.key === null) {
      setDataArr(prev => prev.filter(item => item.id !== data.id));
      setEditingRows(prev => ({ ...prev, [data.id]: false }));
    }
    data.newValue = null;
  };

  const handleRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const newData = e.newData as INamedDatasetRowModel;
    if (!validate(newData)) return;

    setDataArr(prev => {
      const updated = prev.map((item, index) =>
        index === e.index ? { ...item, value: newData.newValue } : item
      );
      propagateChangesStateUp(updated);
      return updated;
    });
  };

  const onRowEditChange = (e: DataTableRowEditEvent) => {
    const newData = dataArr.at(e.index);
    if (!validate(newData!)) {
      const exist = e.data[newData!.id];
      if (!exist) {
        easasAlert({
          summary: T("alert.invalid"),
          severity: "warn",
          detail: T("alert.dataset-must-be-selected"),
          life: 3000,
        });
        return;
      }
    }
    setEditingRows(e.data);
  };

  const handleAdd = () => {
    const newRow = {
      key: `new_dataset_${dataArr.length}`,
      value: { dataset: null },
      newValue: { dataset: null },
      id: v4(),
    } as INamedDatasetRowModel;
    setDataArr(prev => [...prev, newRow]);
    setEditingRows(prev => ({ ...prev, [newRow.id]: true }));
  };

  const handleDelete = (row: INamedDatasetRowModel) => {
    setDataArr(prev => {
      const filtered = prev.filter(item => item.key !== row.key);
      propagateChangesStateUp(filtered);
      return filtered;
    });
  };

  const headerTemplate = (
    <div className="flex justify-content-between">
      <h4>{props.header}</h4>
      <Button icon="pi pi-plus" className="p-button-success" onClick={() => handleAdd()} />
    </div>
  );

  const keyEditorTemplate = (opt: ColumnEditorOptions) => {
    return (
      <InputText
        placeholder={T("word.key")}
        value={opt.rowData.key || ""}
        onChange={e => opt.editorCallback?.(e.target.value)}
      />
    );
  };

  const valueEditorTemplate = (opt: ColumnEditorOptions) => {
    return <DatasetDropdown value={opt.rowData.newValue.dataset} onChange={e => opt.editorCallback?.(e)} />;
  };

  const datasetBodyTemplate = (row: INamedDatasetRowModel) => (
    <DatasetTitle identifier={row.value?.dataset!!} />
  );

  const renderDeleteButton = (row: INamedDatasetRowModel) => {
    return (
      <Button
        icon="pi pi-trash"
        className="const-del-btn  p-button-rounded p-button-text p-button-danger"
        onClick={e => confirmWrapper(e, () => handleDelete(row))}
      />
    );
  };

  return (
    <DataTable
      value={dataArr}
      dataKey="id"
      editMode="row"
      onRowEditInit={handleRowEditInit}
      onRowEditCancel={handleEditCancel}
      onRowEditComplete={handleRowEditComplete}
      onRowEditChange={onRowEditChange}
      editingRows={editingRows}
      header={headerTemplate}
      width="100%"
      className={props.bodyClass}
      size="small"
    >
      <Column field="key" header={T("word.key")} editor={keyEditorTemplate} style={{ width: "20rem" }} />
      <Column
        field="newValue.dataset"
        header={T("word.value")}
        editor={valueEditorTemplate}
        body={datasetBodyTemplate}
      />
      <Column rowEditor align="right" className="w-5rem p-0" />
      <Column body={renderDeleteButton} bodyStyle={{ textAlign: "right", width: "3rem" }} />
    </DataTable>
  );
}

function mapToList(map: Record<string, string>): INamedDatasetRowModel[] {
  return Object.keys(map).map(k => {
    return { key: k, value: { dataset: map[k] }, id: v4() } as INamedDatasetRowModel;
  });
}
